// src/modules/operaciones/inventarios/hooks/useAjustesFiltrados.js
import { useState, useMemo, useEffect, useCallback } from "react";
import { aISO, haceDias, isoDia, comparar } from "../../../../utils/format";
import { POR_PAGINA, txt, ORDEN, ESTADOS } from "../utils/constantes";

export default function useAjustesFiltrados(ajustes) {
  const CACHE_KEY = "cache_ajustes_filtros_v1";
  
  const getCache = () => {
    try {
      const crudo = sessionStorage.getItem(CACHE_KEY);
      if (!crudo) return null;
      const { timestamp, filtros } = JSON.parse(crudo);
      if (Date.now() - timestamp > 5 * 60 * 1000) return null;
      return filtros;
    } catch (e) { return null; }
  };

  const [filtros, setFiltros] = useState(() => {
    const enCache = getCache();
    if (enCache) return enCache;
    return {
      busqueda: "", estado: "todos", bodega: "", concepto: "",
      inicio: aISO(haceDias(30)), fin: aISO(new Date()),
    };
  });

  // Estado para las fechas seleccionadas pero NO aplicadas
  const [fechasTemporales, setFechasTemporales] = useState({ 
    inicio: filtros.inicio, 
    fin: filtros.fin 
  });

  const [orden, setOrden] = useState({ key: "fecha", dir: "desc" });
  const [pagina, setPagina] = useState(1);
  
  const { busqueda, estado, bodega, concepto, inicio, fin } = filtros;

  useEffect(() => {
    const payload = { timestamp: Date.now(), filtros };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  }, [filtros]);

  const actualizar = useCallback((cambios) => setFiltros((f) => ({ ...f, ...cambios })), []);
  const limpiar = () => actualizar({ busqueda: "", estado: "todos", bodega: "", concepto: "" });
  const hayFiltros = Boolean(busqueda) || estado !== "todos" || Boolean(bodega) || Boolean(concepto);

  // Función que se ejecuta al presionar "Aplicar"
  const aplicarFechas = useCallback(() => {
    setFiltros((f) => ({ ...f, inicio: fechasTemporales.inicio, fin: fechasTemporales.fin }));
  }, [fechasTemporales]);

  const cambiarOrden = (key) =>
    setOrden((p) => (p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  useEffect(() => {
    setPagina(1);
  }, [busqueda, estado, bodega, concepto, inicio, fin]);

  const bodegas = useMemo(
    () => [...new Set(ajustes.map((a) => txt(a.BODE_COD)).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es", { numeric: true })),
    [ajustes]
  );

  const conceptos = useMemo(() => {
    const unicos = new Map();
    ajustes.forEach((a) => {
      const c = txt(a.AJUS_CONC);
      if (c && !unicos.has(c.toLowerCase())) unicos.set(c.toLowerCase(), c);
    });
    return [...unicos.values()].sort((a, b) => a.localeCompare(b, "es"));
  }, [ajustes]);

  const filtrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    const conceptoNorm = concepto.trim().toLowerCase();
    const { pasa } = ESTADOS[estado] || ESTADOS.todos;
    const lista = ajustes.filter((a) => {
      if (term) {
        const pajar = [a.AJUS_ID, a.PREF_PRE, a.BODE_COD, a.AJUS_NUMERO, a.AJUS_CONC, a.AJUS_USUARIO].map(txt).join(" ").toLowerCase();
        if (!pajar.includes(term)) return false;
      }
      if (bodega && txt(a.BODE_COD) !== bodega) return false;
      if (conceptoNorm && txt(a.AJUS_CONC).toLowerCase() !== conceptoNorm) return false;
      if (!pasa(a)) return false;
      if (inicio || fin) {
        const dia = isoDia(a.AJUS_FECHA);
        if (!dia || (inicio && dia < inicio) || (fin && dia > fin)) return false;
      }
      return true;
    });
    const valor = ORDEN[orden.key];
    const signo = orden.dir === "asc" ? 1 : -1;
    return [...lista].sort((a, b) => signo * comparar(valor(a), valor(b)));
  }, [ajustes, busqueda, estado, bodega, concepto, inicio, fin, orden]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const paginaActual = Math.min(pagina, totalPaginas);
  const desde = (paginaActual - 1) * POR_PAGINA;

  const chips = useMemo(() => {
    const lista = [];
    if (bodega) lista.push({ key: "bodega", label: `Bodega: ${bodega}`, onRemove: () => actualizar({ bodega: "" }) });
    if (concepto) lista.push({ key: "concepto", label: `Concepto: ${concepto}`, onRemove: () => actualizar({ concepto: "" }) });
    if (estado !== "todos") {
      lista.push({ key: "estado", label: `Estado: ${ESTADOS[estado]?.label || estado}`, onRemove: () => actualizar({ estado: "todos" }) });
    }
    return lista;
  }, [bodega, concepto, estado, actualizar]);

  return {
    filtros, actualizar, limpiar, hayFiltros, orden, cambiarOrden, bodegas, conceptos, filtrados, chips,
    visibles: filtrados.slice(desde, desde + POR_PAGINA),
    paginacion: { paginaActual, totalPaginas, desde, setPagina },
    fechasTemporales, setFechasTemporales, aplicarFechas
  };
}